import React, { useCallback, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useFieldArray, useForm } from 'react-hook-form';
import { VairUser } from 'constants/payloads/vair/playerSearch';
import { VAIR_MINIMUM_PLAYERS } from 'constants/vair';
import { EventGroupFormatsEnum } from 'types/generated/client';
import { getEventExperience } from 'utils/shared/events/getEventExperience';
import { pluralize } from 'utils/shared/pluralize';
import { useViewer } from 'hooks/useViewer';
import { Button, ButtonText } from 'components/Button';
import { useModal } from 'components/modals/Modal';
import ModalShare from 'components/modals/ModalShare';
import classNames from 'styles/utils/classNames';
import AddEventFormField from './AddEventFormField';
import EventGroupRegistrationCard, { PartnerActionMode } from './EventGroupRegistrationCard';
import PhoneNumberSection from './PhoneNumberSection';
import { getDescriptionText, getUsersNeedingPartnerForGroup } from './utils';
import Header from '../Header';
import ModalVairInfo from '../ModalVairInfo';
import {
  GroupsFormData,
  PropsSetRegistrationFormData,
  RegisterProps,
  Steps,
  groupsFormSchema,
} from '../types';

const IS_PHONE_ENABLED = false;

interface Props extends RegisterProps, PropsSetRegistrationFormData {
  name?: string;
  hasPreviousEventRegistration: boolean;
  hasPreviousGroupRegistration: boolean;
  invitationGroupId?: string | null;
  invitationTeamMembers?: { groupId: string; name: string; id: string }[] | null;
  isIgnoreInvitation?: boolean;
  setIsIgnoreInvitation: React.Dispatch<React.SetStateAction<boolean>>;
  isUserOrganizer?: boolean;
  isNextButtonLoading?: boolean;
  onPartnerRequestSent?: () => void;
  vairUsersMap?: Record<string, VairUser>;
  onGameDayClick?: () => void;
}

export default function UserEventList({
  setSteps,
  event,
  name = '',
  registrations,
  setRegistrationFormData,
  handleNext,
  hasPreviousEventRegistration,
  hasPreviousGroupRegistration,
  invitationGroupId,
  invitationTeamMembers,
  isIgnoreInvitation,
  setIsIgnoreInvitation,
  isUserOrganizer,
  isNextButtonLoading,
  onPartnerRequestSent,
  vairUsersMap,
  onGameDayClick,
}: Props) {
  const [shareData, setShareData] = useState<{ title: string; url: string } | null>(null);
  const [activePartnerActionRegistrations, setActivePartnerActionRegistrations] = useState<
    Set<string>
  >(new Set());
  const { userId } = useViewer();

  const handlePartnerActionModeChange = useCallback(
    (registrationId: string, mode: PartnerActionMode) => {
      setActivePartnerActionRegistrations((prev) => {
        const next = new Set(prev);
        if (mode === 'idle') {
          next.delete(registrationId);
        } else {
          next.add(registrationId);
        }
        return next;
      });
    },
    [],
  );

  const isPartnerActionIdle = activePartnerActionRegistrations.size === 0;
  const {
    isOpen: isVairInfoModalOpen,
    openModal: openVairInfoModal,
    closeModal: closeVairInfoModal,
  } = useModal();
  const existingEventCount = registrations?.eventGroupRegistrations.length || 0;

  const isVairRoundRobin = event.groups.some(
    (group) => group.format === EventGroupFormatsEnum.VairRoundRobin,
  );
  const isPrivateWithOutCost = event?.isQuickCreate === true;

  const registeredPlayerCount = event?.registrationsAggregate?.aggregate?.count || 0;

  const handleGameDayClick = () => {
    if (isUserOrganizer && isVairRoundRobin && registeredPlayerCount < VAIR_MINIMUM_PLAYERS) {
      openVairInfoModal();
      return;
    }
    onGameDayClick?.();
  };

  // Pre-select event: prioritize invitationGroupId (from team invitation), then check for single event.
  // Exclude the hasPreviousEventRegistration case — that's handled by the useEffect below.
  const shouldPreSelect =
    invitationGroupId ||
    (!hasPreviousEventRegistration && event.groups.length === 1 && existingEventCount === 0);
  const preSelectedGroupId =
    invitationGroupId || (event.groups.length === 1 ? event.groups[0].id : '');

  const { hasPartners, hasCompetition, noun } = getEventExperience(event);

  const { control, formState, watch, handleSubmit, setValue } = useForm<GroupsFormData>({
    resolver: zodResolver(groupsFormSchema),
    defaultValues: {
      groups: shouldPreSelect
        ? [
            {
              groupId: preSelectedGroupId,
              hasPartner: hasPartners,
              partnerEmail: '',
              partnerName: '',
            },
          ]
        : [],
    },
  });
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'groups',
  });
  const watchedGroups = watch('groups');
  const isGroupsLimit = watchedGroups.length + existingEventCount >= event.groups.length;
  const groupedIdsUsed: string[] = [
    ...watchedGroups.map((group: { groupId: string }) => group.groupId),
    ...(registrations?.eventGroupRegistrations || []).map((gr) => gr.group.id).filter((id) => !!id),
  ];

  React.useEffect(() => {
    if (hasPreviousEventRegistration && !hasPreviousGroupRegistration) {
      // Pre-select event: prioritize invitationGroupId, then check for single event
      const groupId = invitationGroupId || (event.groups.length === 1 ? event.groups[0].id : '');
      append({ groupId, hasPartner: hasPartners, partnerEmail: '', partnerName: '' });
    }
  }, [
    hasPreviousEventRegistration,
    hasPreviousGroupRegistration,
    append,
    event.groups,
    invitationGroupId,
  ]);

  return (
    <>
      <form
        onSubmit={handleSubmit((data) => {
          setRegistrationFormData((prev) => ({ ...prev, groups: data.groups }));
          const wantsPartnerSetup = data.groups.some((g) => g.hasPartner === true);
          if (wantsPartnerSetup) {
            setSteps(Steps.PartnerSetup);
          } else {
            handleNext(data.groups);
          }
        })}
        className="tournament-register-form flex h-full flex-col items-start"
      >
        <div className="flex h-full w-full flex-auto flex-col overflow-y-auto">
          <Header title={`Welcome ${name}`.trim()} className="px-6 pt-6" />
          <div className="px-6 pb-6">
            <div className="typography-product-body mt-ds-xl text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
              {getDescriptionText({
                hasPreviousEventRegistration,
                hasPreviousGroupRegistration,
                eventGroupRegistrations: registrations?.eventGroupRegistrations || [],
                hasPartners,
              })}
            </div>
            {IS_PHONE_ENABLED && <PhoneNumberSection />}
            <div>
              {registrations?.eventGroupRegistrations.map((registration, index) => (
                <EventGroupRegistrationCard
                  key={registration.id}
                  registration={registration}
                  eventTitle={event.title}
                  eventId={event.id}
                  index={index}
                  userName={name}
                  userId={userId}
                  usersNeedingPartner={getUsersNeedingPartnerForGroup(
                    event,
                    registration.group.id,
                    userId,
                  )}
                  receivedPartnerRequests={registrations?.receivedPartnerRequests}
                  onShareDataChange={setShareData}
                  onPartnerActionModeChange={handlePartnerActionModeChange}
                  onPartnerRequestSent={onPartnerRequestSent}
                  vairUsersMap={vairUsersMap}
                  hasPartners={hasPartners}
                />
              ))}
            </div>
            {(fields || []).map((field, index) => (
              <AddEventFormField
                key={field.id}
                field={field}
                index={index}
                control={control}
                formErrors={formState.errors}
                selectedGroupId={watchedGroups[index]?.groupId}
                event={event}
                groupedIdsUsed={groupedIdsUsed}
                existingEventCount={existingEventCount}
                watchedGroupsLength={watchedGroups.length}
                remove={remove}
                invitationTeamMembers={invitationTeamMembers}
                isIgnoreInvitation={isIgnoreInvitation}
                setIsIgnoreInvitation={setIsIgnoreInvitation}
                isVairRoundRobin={isVairRoundRobin}
                isPrivateWithOutCost={isPrivateWithOutCost}
                hasPartner={watchedGroups[index]?.hasPartner ?? hasPartners}
                onHasPartnerChange={(value) => {
                  setValue(`groups.${index}.hasPartner`, value);
                }}
              />
            ))}
            {!isGroupsLimit && (
              <div className="mt-8 flex items-start">
                <ButtonText
                  onClick={() => {
                    append({
                      groupId: '',
                      hasPartner: hasPartners,
                      partnerEmail: '',
                      partnerName: '',
                    });
                  }}
                  className="typography-product-button-label-medium text-color-text-brand dark:text-color-text-brand"
                >
                  + Add event
                </ButtonText>
              </div>
            )}
            <div
              className={classNames('w-full pt-6', watchedGroups.length > 0 ? 'block' : 'hidden')}
            >
              <Button type="submit" variant="inverted" size="lg" loading={isNextButtonLoading}>
                Next
              </Button>
            </div>
          </div>
          {isPartnerActionIdle && (
            <>
              {!!registrations?.eventGroupRegistrations &&
                registrations?.eventGroupRegistrations?.length > 0 && (
                  <div className="mb-ds-xl w-full px-4">
                    <Button
                      variant="primary"
                      size="md"
                      type="button"
                      onClick={() => setSteps(Steps.Withdraw)}
                    >
                      Withdraw from {noun}
                    </Button>
                  </div>
                )}
              {hasCompetition && (
                <div className="bottom-0 mx-auto w-full flex-col items-center justify-center gap-3 border-t border-color-border-input-lightmode bg-color-bg-lightmode-primary p-4 dark:border-color-border-input-darkmode dark:bg-color-bg-darkmode-primary lg:flex">
                  <div className="w-full">
                    <Button variant="brand" size="lg" onClick={handleGameDayClick}>
                      Gameday
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </form>
      <ModalShare
        closeModal={() => setShareData(null)}
        isOpen={!!shareData}
        shareUrl={shareData?.url || ''}
        title={shareData?.title || ''}
      />
      <ModalVairInfo
        isOpen={isVairInfoModalOpen}
        onClose={closeVairInfoModal}
        title="Minimum Players Required"
        message={`Four players are required before starting Gameday. Currently there ${pluralize({
          count: registeredPlayerCount,
          singular: 'is',
          plural: 'are',
          hideCount: true,
        })} ${pluralize({
          count: registeredPlayerCount,
          singular: 'player',
          plural: 'players',
        })} registered.`}
      />
    </>
  );
}
